import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { mediaApi } from './api';
import { keys } from '@/lib/query/keys';
import { useListState } from '@/lib/hooks/useListState';
import { useDebounced } from '@/lib/hooks/useDebounced';
import { DataView } from '@/lib/ui/states';
import { Pagination } from '@/lib/ui/Pagination';
import { Select } from '@/lib/ui/Select';
import { TextInput } from '@/lib/ui/Field';
import { cn } from '@/lib/ui/cn';
import { MonoValue } from '@/lib/ui/Typography';
import { formatBytes } from '@/lib/format';
import type { MediaAsset, MediaKind } from '@/lib/api/types';
import { UploadDropzone } from './UploadDropzone';

const KIND_OPTIONS = [
  { value: '', labelKey: 'media.kindAll' },
  { value: 'image', labelKey: 'media.kindImages' },
  { value: 'svg', labelKey: 'media.kindSvg' },
  { value: 'pdf', labelKey: 'media.kindPdf' },
  { value: 'doc', labelKey: 'media.kindDocuments' },
  { value: 'video', labelKey: 'media.kindVideo' },
];

const KIND_ICON: Record<MediaKind, string> = {
  image: '🖼',
  svg: '⬡',
  pdf: '📄',
  doc: '📝',
  video: '▶',
};

/** Reusable media grid (browser + picker modes). Lazy thumbnails, filters, debounced search. */
export function MediaBrowser({
  onSelect,
  canUpload,
  selectedId,
}: {
  onSelect?: (asset: MediaAsset) => void;
  canUpload?: boolean;
  selectedId?: string | null;
}) {
  const { t } = useTranslation();
  const list = useListState({ kind: '' });
  const [rawSearch, setRawSearch] = useState('');
  const debounced = useDebounced(rawSearch, 300);

  const query = useQuery({
    queryKey: keys.media.list({ ...list.params, q: debounced }),
    queryFn: () => mediaApi.list({ ...list.params, q: debounced || undefined }),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="flex flex-col gap-md">
      {canUpload && <UploadDropzone />}
      <div className="flex flex-wrap items-end gap-sm">
        <div className="w-[160px]">
          <Select
            label={t('media.kind')}
            value={(list.filters.kind as string) || ''}
            onValueChange={(v) => list.setFilter('kind', v)}
            options={KIND_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
          />
        </div>
        <div className="min-w-[200px] flex-1">
          <TextInput
            label={t('common.search')}
            placeholder={t('media.searchPlaceholder')}
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
          />
        </div>
      </div>

      <DataView query={query} isEmpty={(d) => d.data.length === 0}>
        {(d) => (
          <>
            <ul className="grid grid-cols-2 gap-md sm:grid-cols-3 lg:grid-cols-4">
              {d.data.map((asset) => (
                <li key={asset.id}>
                  <button
                    onClick={() => onSelect?.(asset)}
                    className={cn(
                      'group flex w-full flex-col overflow-hidden rounded-md border-[0.5px] border-hairline bg-canvas-paper text-start transition-transform duration-almanac ease-almanac',
                      onSelect && 'cursor-pointer hover:-translate-y-[2px] hover:border-accent-blue',
                      selectedId === asset.id && 'border-accent-blue ring-2 ring-accent-blue/30',
                    )}
                  >
                    <div className="flex aspect-[4/3] items-center justify-center bg-canvas-deep-sand">
                      {asset.kind === 'image' && !asset.is_restricted ? (
                        <img
                          src={asset.derivatives?.thumb?.url ?? asset.url}
                          alt={asset.alt?.en ?? asset.file_name}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-[2rem]" aria-hidden>
                          {KIND_ICON[asset.kind]}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col gap-[2px] p-sm">
                      <span className="truncate text-small text-ink-graphite">{asset.file_name}</span>
                      <span className="font-mono text-label-sm text-ink-mute">
                        {asset.width > 0 && (
                          <MonoValue>
                            {asset.width}×{asset.height}
                          </MonoValue>
                        )}{' '}
                        · {formatBytes(asset.size_bytes)}
                        {asset.is_restricted && ' · 🔒'}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            {d.meta?.pagination && (
              <Pagination
                meta={d.meta.pagination}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
              />
            )}
          </>
        )}
      </DataView>
    </div>
  );
}
